((global: any, factory: (w: any, d: any) => void) => {
    if (!global) {
        throw new Error("ae-login-lib.ts requires window or a global object");
    }

    const document = global.document;
    const aeAppLoader = global.aeAppLoader;

    if (!document) {
        throw new Error("ae-login-lib.ts requires a window or global object with a document");
    }

    aeAppLoader.addAfterInitStartupFunction("LoginLibMain", () => {
        factory(global, document);
    });

})(typeof window !== "undefined" ? window : (this as any),
    (window: any, document: any) => {
        const helpers = window.aeApp.helpers;

        const PASSWORD_TOGGLE_SELECTOR = ".ae-login-password-toggle, .toggle-password-visibility";
        const PASSWORD_FIELD_SELECTOR = ".ae-login-password-field, .input-group";
        const PASSWORD_INPUT_SELECTOR = "input[type=password], input[type=text]";

        const setPasswordVisibility = (toggle: any, visible: boolean) => {
            const container = helpers.closest(toggle, PASSWORD_FIELD_SELECTOR);
            const field = container ? helpers.querySelector(PASSWORD_INPUT_SELECTOR, container) : null;

            if (!field)
                return;

            field.type = visible ? "text" : "password";
            toggle.setAttribute("aria-pressed", visible ? "true" : "false");
        };

        helpers.off(document.body, "click.toggle-password-visibility");
        helpers.on(document.body, "click.toggle-password-visibility", PASSWORD_TOGGLE_SELECTOR, (e: any) => {
            const toggle = e.target;

            setPasswordVisibility(toggle, toggle.getAttribute("aria-pressed") !== "true");
        });

        helpers.off(document.body, "submit.mask-password-visibility");
        helpers.on(document.body, "submit.mask-password-visibility", "form", (e: any) => {
            helpers.querySelectorAll(PASSWORD_TOGGLE_SELECTOR, e.target)
                .forEach((toggle: any) => setPasswordVisibility(toggle, false));
        });

        // Antiforgery-stale protection: when the tab regains visibility (or is
        // restored from bfcache), check whether the user's authenticated status
        // changed out-of-band (e.g. signed in/out in another tab, backchannel
        // logout, session invalidation). If so, reload so forms get a fresh
        // antiforgery token matching the current identity.
        const RELOAD_GUARD_KEY = "ae-login-lib.afr.last-reload";

        interface AuthChangeReloadOptions {
            fingerprintUrl: string;
            minReloadIntervalMs?: number;
        }

        let enabled = false;

        const enableAuthChangeReload = (options: AuthChangeReloadOptions): void => {
            if (enabled) return;
            if (!options || typeof options.fingerprintUrl !== "string" || !options.fingerprintUrl) {
                throw new Error("aeLoginLib.enableAuthChangeReload requires options.fingerprintUrl");
            }
            enabled = true;

            const fingerprintUrl = options.fingerprintUrl;
            const minReloadIntervalMs = typeof options.minReloadIntervalMs === "number"
                ? options.minReloadIntervalMs
                : 2000;

            let cachedAuth: boolean | null = null;
            let inFlight: Promise<boolean | null> | null = null;

            const fetchAuth = (): Promise<boolean | null> => {
                if (inFlight) return inFlight;
                inFlight = fetch(fingerprintUrl, {
                    method: "GET",
                    credentials: "same-origin",
                    headers: {"Accept": "application/json"},
                    cache: "no-store"
                })
                    .then((r: Response) => r.ok ? r.json() : null)
                    .then((data: any) => (data && typeof data.auth === "boolean") ? data.auth as boolean : null)
                    .catch(() => null)
                    .finally(() => {
                        inFlight = null;
                    });
                return inFlight;
            };

            const canReload = (): boolean => {
                try {
                    const last = parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || "0", 10);
                    return (Date.now() - last) >= minReloadIntervalMs;
                } catch (_) {
                    return true;
                }
            };

            const markReload = (): void => {
                try {
                    window.sessionStorage.setItem(RELOAD_GUARD_KEY, Date.now().toString());
                } catch (_) { /* ignore */
                }
            };

            const checkAndReload = async (): Promise<void> => {
                const current = await fetchAuth();
                if (current === null) return;
                if (cachedAuth === null) {
                    cachedAuth = current;
                    return;
                }
                if (current === cachedAuth) return;
                if (!canReload()) {
                    cachedAuth = current;
                    return;
                }
                markReload();
                window.location.reload();
            };

            fetchAuth().then((v: boolean | null) => {
                cachedAuth = v;
            });

            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible")
                    checkAndReload();
            });

            window.addEventListener("pageshow", (e: PageTransitionEvent) => {
                if (e.persisted)
                    checkAndReload();
            });
        };

        // Behaviours the views used to inline in <script> blocks. A CSP nonce is
        // minted per request, so a partial re-rendered for an async form response
        // carries a nonce the already-loaded document's policy no longer accepts
        // and the injected script never runs. Declarative markers scanned from
        // here run under the library's own nonce instead.
        const APPLIED_ATTRIBUTE = "data-ae-login-applied";
        const REDIRECT_URL_ATTRIBUTE = "data-ae-login-redirect-url";
        const REDIRECT_DELAY_ATTRIBUTE = "data-ae-login-redirect-delay";
        const RELOAD_INTERVAL_ATTRIBUTE = "data-ae-login-reload-interval";
        const QR_CODE_URL_ATTRIBUTE = "data-ae-login-qr-code-url";
        const FINGERPRINT_URL_ATTRIBUTE = "data-ae-login-fingerprint-url";

        const DEFAULT_REDIRECT_DELAY_MS = 5000;
        const QR_CODE_RETRY_INTERVAL_MS = 50;
        const QR_CODE_MAX_ATTEMPTS = 200;
        const QR_CODE_SIZE = 150;

        const takeUnappliedElements = (attribute: string): HTMLElement[] => {
            const elements = helpers.querySelectorAll(`[${attribute}]:not([${APPLIED_ATTRIBUTE}])`) as HTMLElement[];

            elements.forEach((element: HTMLElement) => element.setAttribute(APPLIED_ATTRIBUTE, "1"));

            return elements;
        };

        const applyRedirects = (): void => {
            takeUnappliedElements(REDIRECT_URL_ATTRIBUTE).forEach((element: HTMLElement) => {
                const url = element.getAttribute(REDIRECT_URL_ATTRIBUTE);

                if (!url) return;

                const delay = parseInt(element.getAttribute(REDIRECT_DELAY_ATTRIBUTE) || "", 10);

                window.setTimeout(() => {
                    window.location.href = url;
                }, isNaN(delay) ? DEFAULT_REDIRECT_DELAY_MS : delay);
            });
        };

        let periodicReloadScheduled = false;

        const applyPeriodicReloads = (): void => {
            takeUnappliedElements(RELOAD_INTERVAL_ATTRIBUTE).forEach((element: HTMLElement) => {
                const interval = parseInt(element.getAttribute(RELOAD_INTERVAL_ATTRIBUTE) || "", 10);

                if (periodicReloadScheduled || isNaN(interval) || interval <= 0) return;

                periodicReloadScheduled = true;

                window.setInterval(() => {
                    window.location.reload();
                }, interval);
            });
        };

        const renderQrCode = (element: HTMLElement, url: string): void => {
            element.textContent = "";

            new window.QRCode(element, {
                text: url,
                width: QR_CODE_SIZE,
                height: QR_CODE_SIZE
            });
        };

        const applyQrCodes = (): void => {
            takeUnappliedElements(QR_CODE_URL_ATTRIBUTE).forEach((element: HTMLElement) => {
                const url = element.getAttribute(QR_CODE_URL_ATTRIBUTE);

                if (!url) return;

                if (typeof window.QRCode === "function") {
                    renderQrCode(element, url);

                    return;
                }

                let attempts = 0;
                const intervalId = window.setInterval(() => {
                    if (typeof window.QRCode === "function") {
                        window.clearInterval(intervalId);
                        renderQrCode(element, url);
                    } else if (++attempts >= QR_CODE_MAX_ATTEMPTS) {
                        window.clearInterval(intervalId);
                    }
                }, QR_CODE_RETRY_INTERVAL_MS);
            });
        };

        const applyAuthChangeReload = (): void => {
            takeUnappliedElements(FINGERPRINT_URL_ATTRIBUTE).forEach((element: HTMLElement) => {
                const url = element.getAttribute(FINGERPRINT_URL_ATTRIBUTE);

                if (url)
                    enableAuthChangeReload({fingerprintUrl: url});
            });
        };

        const applyBehaviours = (): void => {
            applyRedirects();
            applyPeriodicReloads();
            applyQrCodes();
            applyAuthChangeReload();
        };

        helpers.off(document.body, "ae-app-async-form-end-request.ae-login-behaviours");
        helpers.on(document.body, "ae-app-async-form-end-request.ae-login-behaviours", applyBehaviours);

        applyBehaviours();

        window.aeLoginLib = window.aeLoginLib || {};
        window.aeLoginLib.enableAuthChangeReload = enableAuthChangeReload;
    });
